/**
 * Retro pixel-art boss — a goofy character that blocks the
 * right edge of route 1 / route 2. Each city has TWO unique bosses with
 * city-themed costumes (beret + mustache for Paris, sumo top-knot for Tokyo
 * boss 2, Mountie hat for Toronto, lucha mask for Mexico, etc.).
 *
 * Public API:
 *   drawRiddleBoss(ctx, st)           — paints the boss + speech bubble taunt
 *   isPlayerNearBoss(player, st) → bool — true when player enters the boss zone
 */

const WORLD_W = 1280;
const WORLD_H = 820;

// Boss stands far enough from the route spawn point (x=60) that the player
// must drive a few seconds to reach them. Too close = the bounce-back would
// pin the car at the trigger boundary.
export const BOSS_X = 380;
export const BOSS_Y = WORLD_H / 2;
export const BOSS_R = 70;

export function isPlayerNearBoss(player, st) {
  if (!st || st._riddleRouteIndex == null) return false;
  if (st._riddleRouteIndex < 1) return false;
  if (st._riddleSolved) return false;
  return Math.hypot(player.x - BOSS_X, player.y - BOSS_Y) < BOSS_R;
}

// ---------------------------------------------------------------------------
// 20 unique boss looks — keyed by `${cityId}:${bossIndex}`. Each entry
// declares colors, skin tone, hair color, and a list of `accessories` whose
// drawer functions render extra art on top of the base boxer (mustache,
// beret, crown, sumo mawashi, lucha mask, etc.).
// ---------------------------------------------------------------------------
const BOSS_LOOKS = {
  // ===== PARIS =====
  "paris:0": { // Pierre Pixel — French boxer
    trunkColor: "#0055A4", trunkBand: "#FFFFFF",
    skin: "#E8B894", hair: "#3a2410",
    gloveColor: "#EF4135",
    accessories: ["beret", "frenchMustache"],
    nameColor: "#FFCC00",
  },
  "paris:1": { // Le Berger — wise shepherd boxer
    trunkColor: "#F5F5DC", trunkBand: "#3a2410",
    skin: "#C8A07A", hair: "#A8A8A8",
    gloveColor: "#3a2410",
    accessories: ["fullBeard", "shepherdStaffSash"],
    showBelt: true,
    nameColor: "#FF3B30",
  },

  // ===== NYC =====
  "nyc:0": { // Manhattan Mauler — yellow-cab tough guy
    trunkColor: "#FFCC00", trunkBand: "#000000",
    skin: "#D4A082", hair: "#1a1a1a",
    gloveColor: "#1a1a1a",
    accessories: ["bandana", "brokenNose"],
    nameColor: "#FFCC00",
  },
  "nyc:1": { // Bronx Bomber — backwards-cap brawler
    trunkColor: "#C8102E", trunkBand: "#1E5BC8",
    skin: "#9C6B4E", hair: "#000000",
    gloveColor: "#FFFFFF",
    accessories: ["backwardsCap", "goldChain"],
    showBelt: true,
    nameColor: "#FFFFFF",
  },

  // ===== TOKYO =====
  "tokyo:0": { // Shibuya Slam — neon-haired anime boxer
    trunkColor: "#FF3DF8", trunkBand: "#00F0FF",
    skin: "#FBE0C4", hair: "#1A1A1A",
    gloveColor: "#000000",
    accessories: ["spikyHair", "headband"],
    nameColor: "#00F0FF",
  },
  "tokyo:1": { // Sumo Smasher — actual sumo wrestler (no trunks, mawashi)
    trunkColor: "#E63946", trunkBand: "#FFFFFF", // mawashi colors
    skin: "#F2D6B0", hair: "#1A1A1A",
    gloveColor: "#E63946",
    accessories: ["topknot", "mawashi", "rounderBelly"],
    nameColor: "#FFCC00",
  },

  // ===== LONDON =====
  "london:0": { // Big Ben Brawler — bowler-hat gent
    trunkColor: "#1B3A6E", trunkBand: "#C8102E",
    skin: "#E8C5A0", hair: "#5C3A1E",
    gloveColor: "#1A1A1A",
    accessories: ["bowlerHat", "handlebarMustache", "monocle"],
    nameColor: "#FFFFFF",
  },
  "london:1": { // Royal Roundhouse — purple-and-gold royal
    trunkColor: "#7B2CBF", trunkBand: "#FFCC00",
    skin: "#F2D6B0", hair: "#FFFFFF",
    gloveColor: "#FFCC00",
    accessories: ["crown", "regalMustache"],
    showBelt: true,
    nameColor: "#FFCC00",
  },

  // ===== LA =====
  "la:0": { // Venice Victor — tropical surfer
    trunkColor: "#FF6B9F", trunkBand: "#00CFFF",
    skin: "#D4A074", hair: "#E8B66A", // bleach blonde
    gloveColor: "#FFFFFF",
    accessories: ["sunglasses", "longHair"],
    nameColor: "#FF6B9F",
  },
  "la:1": { // Hollywood Hammer — golden-age megastar
    trunkColor: "#FFCC00", trunkBand: "#FFFFFF",
    skin: "#E8C5A0", hair: "#1A1A1A",
    gloveColor: "#1A1A1A",
    accessories: ["sunglasses", "slickHair", "goldChain"],
    showBelt: true,
    nameColor: "#FFCC00",
  },

  // ===== DETROIT =====
  "detroit:0": { // Motor City Mauler — grease-monkey mechanic
    trunkColor: "#5C3A1E", trunkBand: "#FF6B35",
    skin: "#C8A082", hair: "#8B5A33",
    gloveColor: "#1A1A1A",
    accessories: ["mechanicCap", "stubble"],
    nameColor: "#FF6B35",
  },
  "detroit:1": { // Steel Wheel Wrecker — bald metalhead
    trunkColor: "#3A3A3A", trunkBand: "#C0C8D4",
    skin: "#E8C5A0", hair: null, // bald
    gloveColor: "#1A1A1A",
    accessories: ["aviators", "fullBeard"],
    showBelt: true,
    nameColor: "#C0C8D4",
  },

  // ===== TORONTO =====
  "toronto:0": { // CN Cobra — hockey-mask hitter
    trunkColor: "#C8102E", trunkBand: "#FFFFFF",
    skin: "#E8C5A0", hair: "#5C3A1E",
    gloveColor: "#1A1A1A",
    accessories: ["hockeyMask"],
    nameColor: "#FFFFFF",
  },
  "toronto:1": { // Mountie Mauler — red-tunic Mountie
    trunkColor: "#C8102E", trunkBand: "#FFFFFF",
    skin: "#F2D6B0", hair: "#5C3A1E",
    gloveColor: "#1A1A1A",
    accessories: ["mountieHat", "neatMustache"],
    showBelt: true,
    nameColor: "#FFFFFF",
  },

  // ===== MEXICO CITY =====
  "mexico_city:0": { // Aztec Avenger — feathered Aztec warrior
    trunkColor: "#FFCC00", trunkBand: "#0F8A3E",
    skin: "#A0744A", hair: "#000000",
    gloveColor: "#0F8A3E",
    accessories: ["aztecHeaddress"],
    nameColor: "#FFCC00",
  },
  "mexico_city:1": { // Lucha Libre Lord — masked luchador
    trunkColor: "#FFCC00", trunkBand: "#C8102E",
    skin: "#A0744A", hair: "#000000",
    gloveColor: "#0F8A3E",
    accessories: ["luchaMask"],
    showBelt: true,
    nameColor: "#FFCC00",
  },

  // ===== CAIRO =====
  "cairo:0": { // Pharaoh Fury — Nemes-headdress king
    trunkColor: "#FFFFFF", trunkBand: "#FFCC00",
    skin: "#C8A074", hair: "#000000",
    gloveColor: "#FFCC00",
    accessories: ["nemes", "kohlEyes"],
    nameColor: "#FFCC00",
  },
  "cairo:1": { // Sphinx Striker — kohl-eyed cobra-crowned warrior
    trunkColor: "#1A4A6E", trunkBand: "#FFCC00",
    skin: "#A07452", hair: "#000000",
    gloveColor: "#FFCC00",
    accessories: ["cobraCrown", "kohlEyes"],
    showBelt: true,
    nameColor: "#FFCC00",
  },

  // ===== BEIRUT =====
  "beirut:0": { // Cedar Crusher — full-bearded Lebanese fighter
    trunkColor: "#0F8A3E", trunkBand: "#C8102E",
    skin: "#D4A074", hair: "#3a2410",
    gloveColor: "#1A1A1A",
    accessories: ["fullBeard"],
    nameColor: "#FFCC00",
  },
  "beirut:1": { // Phoenician Phantom — hooded sea-merchant
    trunkColor: "#7B2CBF", trunkBand: "#FFCC00",
    skin: "#C8A074", hair: "#1A1A1A",
    gloveColor: "#FFCC00",
    accessories: ["hood", "fullBeard"],
    showBelt: true,
    nameColor: "#FFFFFF",
  },
};

const DEFAULT_LOOK = {
  trunkColor: "#0F8A3E", trunkBand: "#FFFFFF",
  skin: "#5C3A1E", hair: "#1a0e06",
  gloveColor: "#C8102E",
  accessories: [],
  nameColor: "#FFCC00",
};

function lookFor(cityId, variant) {
  const key = `${cityId}:${variant}`;
  return BOSS_LOOKS[key] || DEFAULT_LOOK;
}

// ---------------------------------------------------------------------------
// Accessory drawers — receive (ctx, cx, cy, headBob, look). Drawn AFTER the
// base body+head so they layer correctly on top.
// ---------------------------------------------------------------------------
const ACC_DRAWERS = {
  beret(ctx, cx, cy, hb) {
    // French black beret with a small stalk on top
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(cx - 22, cy - 56 + hb, 44, 8);
    ctx.fillRect(cx - 18, cy - 60 + hb, 36, 4);
    ctx.fillStyle = "#3a3a3a";
    ctx.fillRect(cx + 4, cy - 64 + hb, 4, 4); // stalk
    // Tiny tricolor pin
    ctx.fillStyle = "#0055A4"; ctx.fillRect(cx + 8, cy - 56 + hb, 2, 2);
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(cx + 10, cy - 56 + hb, 2, 2);
    ctx.fillStyle = "#EF4135"; ctx.fillRect(cx + 12, cy - 56 + hb, 2, 2);
  },
  frenchMustache(ctx, cx, cy, hb) {
    ctx.fillStyle = "#3a2410";
    ctx.fillRect(cx - 8, cy - 30 + hb, 16, 3);
    ctx.fillRect(cx - 10, cy - 31 + hb, 4, 2);
    ctx.fillRect(cx + 6, cy - 31 + hb, 4, 2);
  },
  fullBeard(ctx, cx, cy, hb, look) {
    // Big chunky beard covering chin + lower face
    ctx.fillStyle = look.hair || "#3a2410";
    ctx.fillRect(cx - 14, cy - 24 + hb, 28, 8);
    ctx.fillRect(cx - 16, cy - 22 + hb, 32, 6);
    ctx.fillRect(cx - 12, cy - 18 + hb, 24, 4);
    // mouth gap
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 4, cy - 28 + hb, 8, 2);
  },
  bandana(ctx, cx, cy, hb) {
    ctx.fillStyle = "#C8102E";
    ctx.fillRect(cx - 18, cy - 50 + hb, 36, 4);
    // dots
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 12, cy - 49 + hb, 2, 2);
    ctx.fillRect(cx - 4, cy - 49 + hb, 2, 2);
    ctx.fillRect(cx + 4, cy - 49 + hb, 2, 2);
    ctx.fillRect(cx + 12, cy - 49 + hb, 2, 2);
    // tail flapping right
    ctx.fillStyle = "#C8102E";
    ctx.fillRect(cx + 18, cy - 48 + hb, 6, 8);
  },
  brokenNose(ctx, cx, cy, hb) {
    ctx.fillStyle = "#8B3535";
    ctx.fillRect(cx - 1, cy - 33 + hb, 4, 4);
  },
  backwardsCap(ctx, cx, cy, hb) {
    ctx.fillStyle = "#1E5BC8";
    ctx.fillRect(cx - 18, cy - 56 + hb, 36, 10);
    // brim BACKWARD (left side)
    ctx.fillRect(cx - 24, cy - 50 + hb, 6, 4);
    // NY emblem
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 4, cy - 53 + hb, 8, 4);
    ctx.fillStyle = "#1E5BC8";
    ctx.fillRect(cx - 3, cy - 52 + hb, 2, 2);
    ctx.fillRect(cx + 1, cy - 52 + hb, 2, 2);
  },
  goldChain(ctx, cx, cy) {
    // Y-shaped gold chain on the chest
    ctx.fillStyle = "#FFCC00";
    for (let i = -6; i <= 6; i += 3) {
      ctx.fillRect(cx + i, cy - 16 + Math.abs(i) / 2, 2, 2);
    }
    // pendant
    ctx.fillStyle = "#FFE066";
    ctx.fillRect(cx - 3, cy - 8, 6, 4);
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 2, cy - 7, 4, 2);
  },
  spikyHair(ctx, cx, cy, hb) {
    ctx.fillStyle = "#1A1A1A";
    // Spikes on top
    for (let i = 0; i < 5; i++) {
      const sx = cx - 16 + i * 8;
      ctx.fillRect(sx, cy - 56 + hb, 4, 8);
      ctx.fillRect(sx + 1, cy - 60 + hb, 2, 4);
    }
  },
  headband(ctx, cx, cy, hb) {
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 18, cy - 44 + hb, 36, 4);
    // red dot center
    ctx.fillStyle = "#E63946";
    ctx.fillRect(cx - 2, cy - 43 + hb, 4, 2);
  },
  topknot(ctx, cx, cy, hb) {
    // Sumo top-knot — black bun on shaved scalp
    ctx.fillStyle = "#1A1A1A";
    // bald scalp tone overlay (darker than skin)
    ctx.fillRect(cx - 6, cy - 56 + hb, 12, 6);
    ctx.fillRect(cx - 8, cy - 50 + hb, 16, 2);
    // bun
    ctx.fillRect(cx - 4, cy - 60 + hb, 8, 6);
    ctx.fillRect(cx - 3, cy - 64 + hb, 6, 4);
  },
  mawashi(ctx, cx, cy) {
    // Sumo mawashi — overrides waist band style; thicker, with hanging panel
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 2, cy + 12, 4, 24); // hanging panel
  },
  rounderBelly(ctx, cx, cy, hb, look) {
    // Plump belly bulge over the trunks (sumo body)
    ctx.fillStyle = look.skin;
    ctx.fillRect(cx - 28, cy - 4, 56, 14);
    ctx.strokeStyle = "#000"; ctx.lineWidth = 2;
    ctx.strokeRect(cx - 28, cy - 4, 56, 14);
    // belly button
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 1, cy + 4, 2, 3);
  },
  bowlerHat(ctx, cx, cy, hb) {
    // Black bowler with brim
    ctx.fillStyle = "#1A1A1A";
    ctx.fillRect(cx - 22, cy - 52 + hb, 44, 4); // brim
    ctx.fillRect(cx - 14, cy - 64 + hb, 28, 12); // crown
    ctx.fillRect(cx - 16, cy - 60 + hb, 32, 8);
  },
  handlebarMustache(ctx, cx, cy, hb) {
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(cx - 14, cy - 30 + hb, 28, 3);
    // curled tips up
    ctx.fillRect(cx - 16, cy - 32 + hb, 4, 4);
    ctx.fillRect(cx + 12, cy - 32 + hb, 4, 4);
  },
  monocle(ctx, cx, cy, hb) {
    ctx.strokeStyle = "#FFCC00";
    ctx.lineWidth = 2;
    ctx.strokeRect(cx + 3, cy - 39 + hb, 8, 6);
    // chain
    ctx.strokeStyle = "#888";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx + 11, cy - 38 + hb);
    ctx.lineTo(cx + 18, cy - 30 + hb);
    ctx.stroke();
  },
  crown(ctx, cx, cy, hb) {
    ctx.fillStyle = "#FFCC00";
    // Crown band
    ctx.fillRect(cx - 16, cy - 56 + hb, 32, 6);
    // 5 spikes
    for (let i = 0; i < 5; i++) {
      const sx = cx - 14 + i * 7;
      ctx.fillRect(sx, cy - 64 + hb, 4, 8);
      // gem on each spike
      ctx.fillStyle = ["#C8102E", "#1E5BC8", "#34C759", "#1E5BC8", "#C8102E"][i];
      ctx.fillRect(sx + 1, cy - 62 + hb, 2, 2);
      ctx.fillStyle = "#FFCC00";
    }
  },
  regalMustache(ctx, cx, cy, hb) {
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 12, cy - 30 + hb, 24, 3);
    ctx.fillRect(cx - 14, cy - 31 + hb, 4, 4);
    ctx.fillRect(cx + 10, cy - 31 + hb, 4, 4);
  },
  sunglasses(ctx, cx, cy, hb) {
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 13, cy - 40 + hb, 11, 7);
    ctx.fillRect(cx + 2, cy - 40 + hb, 11, 7);
    ctx.fillRect(cx - 2, cy - 38 + hb, 4, 2); // bridge
    ctx.fillStyle = "#666";
    ctx.fillRect(cx - 11, cy - 39 + hb, 3, 2);
    ctx.fillRect(cx + 4, cy - 39 + hb, 3, 2);
  },
  longHair(ctx, cx, cy, hb, look) {
    ctx.fillStyle = look.hair || "#E8B66A";
    // Hair sides cascading down
    ctx.fillRect(cx - 22, cy - 50 + hb, 4, 28);
    ctx.fillRect(cx + 18, cy - 50 + hb, 4, 28);
  },
  slickHair(ctx, cx, cy, hb) {
    ctx.fillStyle = "#1A1A1A";
    // Slicked-back over-the-top look
    ctx.fillRect(cx - 18, cy - 52 + hb, 36, 6);
    ctx.fillRect(cx - 14, cy - 56 + hb, 28, 4);
  },
  mechanicCap(ctx, cx, cy, hb) {
    // Greasy brown cap
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(cx - 18, cy - 56 + hb, 36, 8);
    ctx.fillRect(cx - 22, cy - 50 + hb, 8, 3); // brim
    // grease stains
    ctx.fillStyle = "#1A1A1A";
    ctx.fillRect(cx - 8, cy - 53 + hb, 4, 2);
    ctx.fillRect(cx + 6, cy - 55 + hb, 3, 2);
  },
  stubble(ctx, cx, cy, hb) {
    ctx.fillStyle = "rgba(60, 40, 20, 0.5)";
    for (let r = 0; r < 3; r++)
      for (let c = 0; c < 8; c++)
        if ((r + c) % 2 === 0) ctx.fillRect(cx - 12 + c * 3, cy - 22 + r * 2 + hb, 1, 1);
  },
  aviators(ctx, cx, cy, hb) {
    // Teardrop aviator sunglasses
    ctx.strokeStyle = "#FFCC00";
    ctx.lineWidth = 2;
    ctx.strokeRect(cx - 13, cy - 40 + hb, 11, 8);
    ctx.strokeRect(cx + 2, cy - 40 + hb, 11, 8);
    ctx.fillStyle = "#0a3050";
    ctx.fillRect(cx - 12, cy - 39 + hb, 9, 6);
    ctx.fillRect(cx + 3, cy - 39 + hb, 9, 6);
  },
  hockeyMask(ctx, cx, cy, hb) {
    // White goalie mask with eye + mouth holes
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 16, cy - 50 + hb, 32, 28);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.strokeRect(cx - 16, cy - 50 + hb, 32, 28);
    // Eye holes
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 11, cy - 41 + hb, 6, 5);
    ctx.fillRect(cx + 5, cy - 41 + hb, 6, 5);
    // Mouth slit
    ctx.fillRect(cx - 6, cy - 28 + hb, 12, 2);
    // Vertical stripes
    ctx.strokeStyle = "#C8102E";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - 4, cy - 50 + hb);
    ctx.lineTo(cx - 4, cy - 22 + hb);
    ctx.moveTo(cx + 4, cy - 50 + hb);
    ctx.lineTo(cx + 4, cy - 22 + hb);
    ctx.stroke();
  },
  mountieHat(ctx, cx, cy, hb) {
    // Stetson — wide brim + tall crown
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(cx - 26, cy - 52 + hb, 52, 4); // brim
    ctx.fillRect(cx - 14, cy - 64 + hb, 28, 14); // crown
    // Yellow band
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 14, cy - 54 + hb, 28, 2);
  },
  neatMustache(ctx, cx, cy, hb) {
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(cx - 8, cy - 30 + hb, 16, 2);
  },
  aztecHeaddress(ctx, cx, cy, hb) {
    // Feathered headdress — green/red/gold fan
    const colors = ["#0F8A3E", "#FFCC00", "#C8102E", "#FFCC00", "#0F8A3E"];
    for (let i = 0; i < 5; i++) {
      const fx = cx - 22 + i * 9;
      ctx.fillStyle = colors[i];
      ctx.fillRect(fx, cy - 70 + hb, 6, 22);
      ctx.fillRect(fx + 1, cy - 72 + hb, 4, 4);
    }
    // Headband across forehead
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(cx - 20, cy - 50 + hb, 40, 4);
    // gold disc center
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 3, cy - 50 + hb, 6, 4);
  },
  luchaMask(ctx, cx, cy, hb) {
    // Multi-color lucha mask covering face
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 18, cy - 50 + hb, 36, 32);
    // Red zigzag pattern
    ctx.fillStyle = "#C8102E";
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(cx - 14 + i * 8, cy - 44 + hb, 4, 2);
      ctx.fillRect(cx - 18 + i * 8, cy - 28 + hb, 4, 2);
    }
    // Eye holes
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 11, cy - 38 + hb, 6, 4);
    ctx.fillRect(cx + 5, cy - 38 + hb, 6, 4);
    // Mouth slit
    ctx.fillRect(cx - 4, cy - 26 + hb, 8, 2);
    // Outline
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.strokeRect(cx - 18, cy - 50 + hb, 36, 32);
  },
  nemes(ctx, cx, cy, hb) {
    // Egyptian Nemes headdress (Tut style) — striped gold/blue side flaps
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 20, cy - 56 + hb, 40, 12);
    // Side flaps draping down
    ctx.fillRect(cx - 24, cy - 50 + hb, 6, 24);
    ctx.fillRect(cx + 18, cy - 50 + hb, 6, 24);
    // Blue stripes
    ctx.fillStyle = "#1A4A6E";
    for (let i = 0; i < 4; i++) ctx.fillRect(cx - 18 + i * 10, cy - 56 + hb, 2, 12);
    for (let r = 0; r < 4; r++) ctx.fillRect(cx - 24, cy - 48 + hb + r * 5, 6, 2);
    for (let r = 0; r < 4; r++) ctx.fillRect(cx + 18, cy - 48 + hb + r * 5, 6, 2);
    // Cobra (uraeus) center
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(cx - 2, cy - 60 + hb, 4, 6);
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 1, cy - 62 + hb, 2, 4);
  },
  kohlEyes(ctx, cx, cy, hb) {
    ctx.fillStyle = "#000";
    ctx.fillRect(cx - 13, cy - 36 + hb, 9, 1);
    ctx.fillRect(cx + 4, cy - 36 + hb, 9, 1);
    // wing tips
    ctx.fillRect(cx - 15, cy - 37 + hb, 2, 1);
    ctx.fillRect(cx + 13, cy - 37 + hb, 2, 1);
  },
  cobraCrown(ctx, cx, cy, hb) {
    ctx.fillStyle = "#FFCC00";
    ctx.fillRect(cx - 14, cy - 56 + hb, 28, 4);
    // Cobra rising
    ctx.fillRect(cx - 3, cy - 64 + hb, 6, 8);
    ctx.fillRect(cx - 5, cy - 60 + hb, 2, 4);
    ctx.fillRect(cx + 3, cy - 60 + hb, 2, 4);
    // hood
    ctx.fillStyle = "#1A4A6E";
    ctx.fillRect(cx - 5, cy - 58 + hb, 10, 2);
  },
  hood(ctx, cx, cy, hb, look) {
    ctx.fillStyle = look.trunkColor;
    ctx.fillRect(cx - 22, cy - 56 + hb, 44, 14);
    // hood drape
    ctx.fillRect(cx - 24, cy - 44 + hb, 4, 16);
    ctx.fillRect(cx + 20, cy - 44 + hb, 4, 16);
    // gold trim
    ctx.fillStyle = look.trunkBand;
    ctx.fillRect(cx - 22, cy - 44 + hb, 44, 2);
  },
  shepherdStaffSash(ctx, cx, cy) {
    // Diagonal sash across chest
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-Math.PI / 6);
    ctx.fillStyle = "#5C3A1E";
    ctx.fillRect(-30, -2, 60, 4);
    ctx.restore();
  },
};

function drawAccessories(ctx, cx, cy, hb, look) {
  for (const acc of look.accessories || []) {
    const fn = ACC_DRAWERS[acc];
    if (fn) fn(ctx, cx, cy, hb, look);
  }
}

// ---------------------------------------------------------------------------
// Main draw
// ---------------------------------------------------------------------------
export function drawRiddleBoss(ctx, st) {
  if (!st || st._riddleRouteIndex == null || st._riddleRouteIndex < 1 || st._riddleSolved) return;
  const cityId = st.cityId || "default";
  const variant = (st._riddleVariant ?? 0) % 2;
  const t = performance.now() / 200;
  const bob = Math.round(Math.sin(t) * 2);
  const cx = BOSS_X;
  const cy = BOSS_Y + bob;

  ctx.save();

  // Trigger-radius ring (subtle pulse) — only for the in-game boss, not
  // for the gallery portrait.
  const ringPulse = (Math.sin(performance.now() / 350) + 1) / 2;
  ctx.strokeStyle = `rgba(255, 60, 60, ${0.25 + ringPulse * 0.35})`;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(BOSS_X, BOSS_Y, BOSS_R, 0, Math.PI * 2);
  ctx.stroke();

  drawBossPortrait(ctx, cityId, variant, cx, cy, {
    bossName: st._riddleBossName,
    speechBubble: variant === 1 ? "I'M THE CHAMP\nPROVE IT!" : "ANSWER MY RIDDLE\nOR EAT CONCRETE!",
    showShadow: true,
    showName: true,
  });

  ctx.restore();
}

/**
 * Standalone boss-portrait drawer — paints a single boss at (cx, cy) with
 * full city-themed accessories. Used by both the in-game boss and the
 * Boss Gallery page.
 *
 * opts: {
 *   bossName?: string         — name banner above the boss (skipped if showName false)
 *   speechBubble?: string     — taunt bubble; multi-line via "\n"
 *   showShadow?: boolean      — drop shadow under the boots (default true)
 *   showName?: boolean        — render bossName banner (default true)
 *   silhouette?: boolean      — tints everything dark for "undefeated" mode
 *   scale?: number            — uniform scale factor (default 1)
 * }
 */
export function drawBossPortrait(ctx, cityId, variant, cx, cy, opts = {}) {
  const v = (variant ?? 0) % 2;
  const baseLook = lookFor(cityId, v);
  const look = opts.silhouette ? silhouetteLook(baseLook) : baseLook;
  const showShadow = opts.showShadow !== false;
  const showName = opts.showName !== false;
  const scale = opts.scale ?? 1;
  const t = performance.now() / 200;
  const headBob = Math.round(Math.sin(t * 1.3) * 1);

  ctx.save();
  if (scale !== 1) {
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    cx = 0; cy = 0;
  }

  // Shadow
  if (showShadow) {
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fillRect(cx - 26, cy + 38, 52, 8);
  }

  // Trunks
  ctx.fillStyle = look.trunkColor;
  ctx.fillRect(cx - 22, cy + 8, 44, 28);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeRect(cx - 22, cy + 8, 44, 28);
  // Waist band + gold buckle
  ctx.fillStyle = look.trunkBand;
  ctx.fillRect(cx - 22, cy + 8, 44, 4);
  ctx.fillStyle = opts.silhouette ? "#3a3a3a" : "#FFCC00";
  ctx.fillRect(cx - 4, cy + 8, 8, 4);

  // Legs / boots
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(cx - 18, cy + 36, 14, 10);
  ctx.fillRect(cx + 4, cy + 36, 14, 10);
  ctx.fillStyle = opts.silhouette ? "#3a3a3a" : "#FFFFFF";
  ctx.fillRect(cx - 18, cy + 38, 14, 2);
  ctx.fillRect(cx + 4, cy + 38, 14, 2);

  // Torso (skin tone per look)
  ctx.fillStyle = look.skin;
  ctx.fillRect(cx - 26, cy - 18, 52, 28);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeRect(cx - 26, cy - 18, 52, 28);
  // Pec definition (slightly darker tone of skin)
  ctx.fillStyle = darkenHex(look.skin, 0.18);
  ctx.fillRect(cx - 24, cy - 14, 22, 8);
  ctx.fillRect(cx + 2, cy - 14, 22, 8);

  // Championship belt (variant 1 only)
  if (look.showBelt) {
    ctx.fillStyle = opts.silhouette ? "#3a3a3a" : "#FFCC00";
    ctx.fillRect(cx - 26, cy + 4, 52, 8);
    ctx.strokeStyle = "#000"; ctx.lineWidth = 2;
    ctx.strokeRect(cx - 26, cy + 4, 52, 8);
    ctx.fillStyle = opts.silhouette ? "#555" : "#FFFFFF";
    ctx.fillRect(cx - 5, cy + 5, 10, 6);
    ctx.fillStyle = opts.silhouette ? "#3a3a3a" : "#FFCC00";
    ctx.fillRect(cx - 3, cy + 6, 6, 4);
  }

  // Head
  ctx.fillStyle = look.skin;
  ctx.fillRect(cx - 18, cy - 50 + headBob, 36, 32);
  ctx.strokeRect(cx - 18, cy - 50 + headBob, 36, 32);
  if (look.hair) {
    ctx.fillStyle = look.hair;
    ctx.fillRect(cx - 18, cy - 50 + headBob, 36, 8);
  }

  // Default eyes
  ctx.fillStyle = opts.silhouette ? "#222" : "#FFFFFF";
  ctx.fillRect(cx - 12, cy - 38 + headBob, 8, 4);
  ctx.fillRect(cx + 4, cy - 38 + headBob, 8, 4);
  ctx.fillStyle = "#000";
  ctx.fillRect(cx - 8, cy - 38 + headBob, 4, 4);
  ctx.fillRect(cx + 4, cy - 38 + headBob, 4, 4);
  // Default mouth
  ctx.fillStyle = "#000";
  ctx.fillRect(cx - 10, cy - 28 + headBob, 20, 4);
  ctx.fillStyle = opts.silhouette ? "#222" : "#FFFFFF";
  ctx.fillRect(cx - 8, cy - 27 + headBob, 4, 2);
  ctx.fillRect(cx, cy - 27 + headBob, 4, 2);

  // City-themed accessories ON TOP of base body+head
  drawAccessories(ctx, cx, cy, headBob, look);

  // Boxing gloves — punch-pulse forward
  const punch = Math.sin(t * 1.5);
  const gx = cx - 40 + punch * 4;
  const gx2 = cx + 28 - punch * 4;
  ctx.fillStyle = look.gloveColor;
  ctx.fillRect(gx, cy - 10, 18, 18);
  ctx.fillRect(gx2, cy - 10, 18, 18);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeRect(gx, cy - 10, 18, 18);
  ctx.strokeRect(gx2, cy - 10, 18, 18);
  ctx.fillStyle = opts.silhouette ? "#3a3a3a" : "#FFFFFF";
  ctx.fillRect(gx + 7, cy - 8, 4, 14);
  ctx.fillRect(gx2 + 7, cy - 8, 4, 14);

  // Boss name banner above
  if (showName && opts.bossName) {
    ctx.font = "bold 11px 'Press Start 2P', monospace";
    ctx.textAlign = "center";
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#000";
    ctx.fillStyle = look.nameColor || "#FFCC00";
    ctx.strokeText(opts.bossName, cx, cy - 80);
    ctx.fillText(opts.bossName, cx, cy - 80);
  }

  // Speech bubble
  if (opts.speechBubble) {
    drawSpeechBubble(ctx, cx + 80, cy - 30, opts.speechBubble);
  }

  ctx.restore();
}

// Convert a look object to a "silhouette" version — every visible color is
// flattened to a dark gray scale so the boss reads as locked/undefeated.
function silhouetteLook(look) {
  const dark = "#1c1a22";
  const darker = "#0f0d12";
  return {
    ...look,
    trunkColor: dark,
    trunkBand: darker,
    skin: "#2a2630",
    hair: look.hair ? "#0a080d" : null,
    gloveColor: dark,
    nameColor: "#444",
  };
}

// Tiny helper — darken a hex color by `pct` (0..1).
function darkenHex(hex, pct) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const r = Math.max(0, Math.round(parseInt(m[1], 16) * (1 - pct)));
  const g = Math.max(0, Math.round(parseInt(m[2], 16) * (1 - pct)));
  const b = Math.max(0, Math.round(parseInt(m[3], 16) * (1 - pct)));
  return `rgb(${r},${g},${b})`;
}

function drawSpeechBubble(ctx, x, y, text) {
  const lines = text.split("\n");
  const bw = 220;
  const bh = 44;
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.fillRect(x - 10, y - 22, bw, bh);
  ctx.strokeRect(x - 10, y - 22, bw, bh);
  ctx.beginPath();
  ctx.moveTo(x - 10, y);
  ctx.lineTo(x - 24, y + 8);
  ctx.lineTo(x - 4, y + 4);
  ctx.closePath();
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#000";
  ctx.font = "bold 9px 'Press Start 2P', monospace";
  ctx.textAlign = "left";
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], x - 4, y - 8 + i * 14);
  }
}
